import { QueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { Id, getData, getQueryKey } from '@feature/utils';
import { Validation, ValidationRequest } from '@feature/validation/ValidationTypes';

export interface ValidationApi {
  getHistory: (entityId: Id) => Promise<Validation[]>;
  getCurrent: (entityId: Id) => Promise<Validation | null>;
  validate: (entityId: Id, request: ValidationRequest) => Promise<unknown>;
}

export function generateValidationApi(baseUrl: string): ValidationApi {
  return {
    getCurrent: (entityId: Id) =>
      axios
        .get<Validation | null>(`${baseUrl}/${entityId}/current`)
        .then(getData)
        .then(data => (!data ? null : data)),
    getHistory: (entityId: Id) => axios.get<Validation[]>(`${baseUrl}/${entityId}/history`).then(getData),
    validate: (entityId: Id, request: ValidationRequest) =>
      axios.post<unknown>(`${baseUrl}/${entityId}/validate`, request).then(getData),
  };
}

export function generateValidationOptions(keyRoot: string, api: ValidationApi) {
  const validationKey = getQueryKey(keyRoot);

  return {
    getHistoryOptions: (entityId: Id) => ({
      queryKey: validationKey.as([entityId, 'history']),
      queryFn: () => api.getHistory(entityId),
    }),
    getCurrentOptions: (entityId: Id) => ({
      queryKey: validationKey.as([entityId, 'current']),
      queryFn: () => api.getCurrent(entityId),
    }),
    validateOptions: (entityId: Id, queryClient: QueryClient) => ({
      mutationFn: (request: ValidationRequest) => api.validate(entityId, request),
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: validationKey.as([entityId]),
        }),
    }),
  };
}
